import { acceptCompletion, autocompletion, closeBrackets } from '@codemirror/autocomplete';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { bracketMatching, foldGutter, indentOnInput, indentUnit } from '@codemirror/language';
import { searchKeymap } from '@codemirror/search';
import { Compartment, EditorState, StateEffect, StateField, type Extension } from '@codemirror/state';
import {
  EditorView,
  drawSelection,
  dropCursor,
  highlightActiveLine,
  highlightActiveLineGutter,
  keymap,
  lineNumbers,
  scrollPastEnd,
  tooltips,
} from '@codemirror/view';
import { useStore } from '@nanostores/react';
import { memo, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { BinaryContent } from './BinaryContent';
import { aiTooltipStateEffect, aiTooltipStateField } from './aiTooltip';
import { getTheme, reconfigureTheme } from './cm-theme';
import { dotEnvMaskExtension } from './dotenv-mask';
import { indentKeyBinding } from './indent';
import { getLanguage } from './languages';
import { readOnlyTooltipStateEffect, readOnlyTooltipStateField } from './readOnlyTooltip';
import { filePathStateField, updateFilePath } from './state';
import { isImageFile } from './utils';
import { editorSettingsStore } from '~/lib/stores/settings';
import { workbenchStore } from '~/lib/stores/workbench';
import type { Theme } from '~/types/theme';
import { classNames } from '~/utils/classNames';
import { useInitRef } from '~/utils/init-ref';
import { createScopedLogger, renderLogger } from '~/utils/logger';

const logger = createScopedLogger('CodeMirrorEditor');

export interface EditorDocument {
  value: string;
  isBinary: boolean;
  filePath: string;
  scroll?: ScrollPosition;
}

export interface EditorSettings {
  fontSize?: string;
  gutterFontSize?: string;
  tabSize?: number;
}

export interface ScrollPosition {
  top: number;
  left: number;
}

interface CodeMirrorEditorProps {
  theme: Theme;
  doc?: EditorDocument;
  className?: string;
  settings?: EditorSettings;
}

const onEditorChange = (content: string) => {
  workbenchStore.setCurrentDocumentContent(content);
};

const onEditorScroll = (position: ScrollPosition) => {
  workbenchStore.setCurrentDocumentScrollPosition(position);
};

const editableStateEffect = StateEffect.define<boolean>();

const editableStateField = StateField.define<boolean>({
  create() {
    return true;
  },
  update(value, transaction) {
    for (const effect of transaction.effects) {
      if (effect.is(editableStateEffect)) {
        return effect.value;
      }
    }

    return value;
  },
});

const languageCompartment = new Compartment();
const lineWrappingCompartment = new Compartment();

export const CodeMirrorEditor = memo(({ theme, settings, className = '', doc }: CodeMirrorEditorProps) => {
  renderLogger.trace('CodeMirrorEditor');

  const isProcessing = useStore(workbenchStore.isProcessing);
  const editable = !isProcessing && doc !== undefined && !doc.isBinary;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorStatesRef = useInitRef(() => new Map<string, EditorState>());

  const viewRef = useRef<EditorView>();

  // init editor
  useEffect(() => {
    const view = (viewRef.current = new EditorView({
      parent: containerRef.current!,
      dispatchTransactions(transactions) {
        const isUserEdit = transactions.some((transaction) => {
          return transaction.isUserEvent('input') || transaction.isUserEvent('delete');
        });

        view.update(transactions);

        if (doc) {
          editorStatesRef.current!.set(doc.filePath, view.state);
        }

        if (isUserEdit) {
          onEditorChange(view.state.doc.toString());
        }
      },
    }));

    workbenchStore.setEditorView(view);
    workbenchStore.setEditorViewReady();

    const unsubscribeFromSettings = editorSettingsStore.subscribe((settings) => {
      viewRef.current!.dispatch({
        effects: [lineWrappingCompartment.reconfigure(settings.lineWrapping ? [EditorView.lineWrapping] : [])],
      });
    });

    const unsubscribeFromCurrentView = workbenchStore.currentView.subscribe(() => {
      // we hide all tooltips whenever the workbench view changes
      viewRef.current!.dispatch({
        effects: [readOnlyTooltipStateEffect.of(false), aiTooltipStateEffect.of(false)],
      });
    });

    return () => {
      viewRef.current?.destroy();
      viewRef.current = undefined;
      workbenchStore.setEditorView(null);

      unsubscribeFromCurrentView();
      unsubscribeFromSettings();
    };
  }, []);

  // update theme
  useEffect(() => {
    viewRef.current?.dispatch({
      effects: [reconfigureTheme(theme)],
    });
  }, [theme]);

  // update readonly status
  useEffect(() => {
    viewRef.current?.dispatch({
      effects: [editableStateEffect.of(editable)],
    });
  }, [editable]);

  // change document
  useEffect(() => {
    const view = viewRef.current!;

    if (!doc || doc.isBinary) {
      const state = newEditorState(undefined, theme, settings, [languageCompartment.of([])]);

      view.setState(state);

      setEmptyDocument(view);

      return;
    }

    if (doc.filePath === '') {
      logger.warn('File path should not be empty');
    }

    const wrapLines = editorSettingsStore.get().lineWrapping;

    let state = editorStatesRef.current.get(doc.filePath);

    if (!state) {
      state = newEditorState(doc.value, theme, settings, [
        languageCompartment.of([]),
        lineWrappingCompartment.of(wrapLines ? [EditorView.lineWrapping] : []),
      ]);

      editorStatesRef.current.set(doc.filePath, state);

      view.setState(state);
    }

    // set state if not streaming
    if (!workbenchStore.isProcessing.get()) {
      view.setState(state);
    }

    view.dispatch({
      effects: [updateFilePath.of(doc.filePath)],
    });

    getLanguage(doc.filePath).then((languageSupport) => {
      if (!languageSupport) {
        workbenchStore.setEditorViewReady();
        return;
      }

      view.dispatch({
        effects: [languageCompartment.reconfigure([languageSupport]), reconfigureTheme(theme)],
      });

      requestAnimationFrame(() => {
        if (workbenchStore.isProcessing.get()) {
          return;
        }

        view.scrollDOM.scrollTo(doc.scroll?.left ?? 0, doc.scroll?.top ?? 0);
        workbenchStore.setEditorViewReady();
      });
    });
  }, [doc?.filePath]);

  // update document value
  useEffect(() => {
    const view = viewRef.current!;
    const currentText = view.state.doc.toString();

    if (doc === undefined || doc.value === currentText) {
      return;
    }

    if (doc.value.startsWith(currentText)) {
      view.dispatch({
        changes: {
          from: view.state.doc.length,
          insert: doc.value.slice(currentText.length),
        },
      });
    } else {
      view.dispatch({
        selection: { anchor: 0 },
        changes: {
          from: 0,
          to: view.state.doc.length,
          insert: doc.value,
        },
      });
    }
  }, [doc?.value]);

  return (
    <div className={classNames('relative h-full', className)}>
      {doc?.isBinary &&
        (doc && isImageFile(doc.filePath) ? (
          <img src={doc.value} className="m-auto object-contain" />
        ) : (
          <BinaryContent />
        ))}
      <div
        className={classNames('h-full overflow-hidden', {
          hidden: doc?.isBinary,
        })}
        ref={containerRef}
      />
    </div>
  );
});

export default CodeMirrorEditor;

CodeMirrorEditor.displayName = 'CodeMirrorEditor';

function newEditorState(
  content: string | undefined,
  theme: Theme,
  settings: EditorSettings | undefined,
  extensions: Extension[],
) {
  return EditorState.create({
    doc: content ?? '',
    extensions: [
      EditorView.contentAttributes.of({ 'aria-label': 'Editor' }),
      EditorView.domEventHandlers({
        scroll: (event, view) => {
          if (event.target !== view.scrollDOM) {
            return;
          }

          onEditorScroll({ left: view.scrollDOM.scrollLeft, top: view.scrollDOM.scrollTop });
        },
        mousedown: (_, view) => {
          view.dispatch({
            effects: [aiTooltipStateEffect.of(false)],
          });

          return false;
        },
        mouseup: (_, view) => {
          view.dispatch({
            effects: [aiTooltipStateEffect.of(!view.state.selection.main.empty && view.hasFocus)],
          });

          return false;
        },
        keyup: (event, view) => {
          if (event.key === 'Shift') {
            view.dispatch({
              effects: [aiTooltipStateEffect.of(!view.state.selection.main.empty && event.key === 'Shift')],
            });
          }

          return false;
        },
        keydown: (event, view) => {
          if (view.state.readOnly) {
            view.dispatch({
              effects: [
                readOnlyTooltipStateEffect.of({
                  show: event.key !== 'Escape',
                  text: content === undefined ? 'No file selected' : undefined,
                }),
              ],
            });

            return true;
          }

          return false;
        },
      }),
      getTheme(theme, settings),
      history(),
      keymap.of([
        ...defaultKeymap,
        ...historyKeymap,
        ...searchKeymap,
        { key: 'Tab', run: acceptCompletion },
        {
          key: 'Mod-s',
          preventDefault: true,
          run: () => {
            workbenchStore.saveCurrentDocument().catch(() => {
              toast.error('Failed to update file content');
            });
            return true;
          },
        },
        indentKeyBinding,
      ]),
      indentUnit.of('  '),
      autocompletion({
        closeOnBlur: false,
      }),
      tooltips({
        position: 'absolute',
        parent: document.body,
        tooltipSpace: (view) => {
          const rect = view.dom.getBoundingClientRect();

          return {
            top: rect.top - 50,
            left: rect.left,
            bottom: rect.bottom,
            right: rect.right + 10,
          };
        },
      }),
      closeBrackets(),
      lineNumbers(),
      scrollPastEnd(),
      dropCursor(),
      drawSelection(),
      bracketMatching(),
      EditorState.tabSize.of(settings?.tabSize ?? 2),
      indentOnInput(),
      filePathStateField,
      aiTooltipStateField,
      readOnlyTooltipStateField,
      editableStateField,
      EditorState.readOnly.from(editableStateField, (editable) => !editable),
      highlightActiveLineGutter(),
      highlightActiveLine(),
      foldGutter({
        markerDOM: (open) => {
          const icon = document.createElement('div');

          icon.className = `fold-icon ${open ? 'i-ph-caret-down-bold' : 'i-ph-caret-right-bold'}`;

          return icon;
        },
      }),
      dotEnvMaskExtension,
      ...extensions,
    ],
  });
}

function setEmptyDocument(view: EditorView) {
  view.dispatch({
    selection: { anchor: 0 },
    effects: [editableStateEffect.of(false)],
    changes: {
      from: 0,
      to: view.state.doc.length,
      insert: '',
    },
  });

  view.scrollDOM.scrollTo(0, 0);
}
